import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from '../ui/Modal';
import { effectiveSetSeconds } from '../../utils/workoutDuration';
import { uploadExerciseImage } from '../../utils/uploadExerciseImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fixed reference data
const PRIMARY_MUSCLE_GROUPS = [
  'Chest', 'Triceps', 'Deltoids', 'Back', 'Lats', 
  'Biceps', 'Forearms', 'Quadriceps', 'Hamstrings', 'Glutes', 'Calves', 'Core'
];

const MOVEMENT_PATTERNS = [
  { id: 'squat', name: 'Squat' },
  { id: 'hinge', name: 'Hinge' },
  { id: 'horizontal_push', name: 'Horizontal Push' },
  { id: 'vertical_push', name: 'Vertical Push' },
  { id: 'horizontal_pull', name: 'Horizontal Pull' },
  { id: 'vertical_pull', name: 'Vertical Pull' },
  { id: 'carry', name: 'Carry' },
  { id: 'rotation', name: 'Rotation' },
  { id: 'anti_rotation', name: 'Anti-Rotation' },
  { id: 'isolation', name: 'Isolation' },
];

const EXERCISE_TYPES = [
  { id: 'strength', name: 'Strength' },
  { id: 'plyometric', name: 'Plyometric' },
  { id: 'stretch', name: 'Stretch' },
  { id: 'mobility', name: 'Mobility' },
  { id: 'yoga', name: 'Yoga' },
  { id: 'cardio', name: 'Cardio' },
  { id: 'calisthenics', name: 'Calisthenics' },
  { id: 'rehab', name: 'Rehab' },
];

// Phase 1A: Final 19 exercise families (locked by user).
// Free text is still accepted by the backend; this list powers the dropdown
// suggestions for consistent classification.
const EXERCISE_FAMILIES = [
  { id: 'bench_press', name: 'Bench Press' },
  { id: 'overhead_press', name: 'Overhead Press' },
  { id: 'chest_fly', name: 'Chest Fly' },
  { id: 'dip', name: 'Dip' },
  { id: 'row', name: 'Row' },
  { id: 'pulldown_pullup', name: 'Pulldown / Pull-Up' },
  { id: 'rear_delt', name: 'Rear Delt' },
  { id: 'lateral_raise', name: 'Lateral Raise' },
  { id: 'front_raise', name: 'Front Raise' },
  { id: 'shrug', name: 'Shrug' },
  { id: 'curl', name: 'Curl' },
  { id: 'tricep_extension', name: 'Tricep Extension' },
  { id: 'squat', name: 'Squat' },
  { id: 'lunge', name: 'Lunge' },
  { id: 'hinge', name: 'Hinge' },
  { id: 'leg_curl', name: 'Leg Curl' },
  { id: 'leg_extension', name: 'Leg Extension' },
  { id: 'calf_raise', name: 'Calf Raise' },
  { id: 'core', name: 'Core' },
];

const CATEGORIES = [
  { id: 'chest', name: 'Chest' },
  { id: 'back', name: 'Back' },
  { id: 'legs', name: 'Legs' },
  { id: 'shoulders', name: 'Shoulders' },
  { id: 'arms', name: 'Arms' },
  { id: 'core', name: 'Core' },
  { id: 'cardio', name: 'Cardio' },
  { id: 'full-body', name: 'Full Body' },
  { id: 'stretching', name: 'Stretching' },
  { id: 'plyometrics', name: 'Plyometrics' },
  { id: 'neck', name: 'Neck' },
];

// Fields the AI auto-fill can complete (mirrors backend field_specs)
const AI_FILLABLE_FIELDS = [
  'name_es', 'difficulty', 'movement_pattern', 'exercise_type', 'primary_muscle_groups',
  'execution_steps', 'execution_steps_es', 'form_tips', 'form_tips_es',
  'common_mistakes', 'common_mistakes_es', 'benefits', 'benefits_es',
  'target_muscles', 'target_muscles_es', 'synergist_muscles', 'synergist_muscles_es',
  'stabilizer_muscles', 'stabilizer_muscles_es',
];

const ExerciseEditModal = ({ exercise, onSave, onClose, language, t, isCreate: isCreateProp }) => {
  const isCreate = isCreateProp ?? !exercise?.id;
  const [equipmentList, setEquipmentList] = useState([]);
  const [activeTab, setActiveTab] = useState('basic'); // basic, execution, muscles, advanced
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiFilled, setAiFilled] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [imgError, setImgError] = useState(null);
  const [formData, setFormData] = useState({
    name: exercise?.name || '',
    name_es: exercise?.name_es || '',
    category: exercise?.category || '',
    difficulty: exercise?.difficulty || '',
    equipment_ids: exercise?.equipment_ids || [],
    primary_muscle_groups: exercise?.primary_muscle_groups || [],
    movement_pattern: exercise?.movement_pattern || '',
    exercise_type: exercise?.exercise_type || '',
    exercise_family: exercise?.exercise_family || '',
    avg_set_seconds: exercise?.avg_set_seconds ?? '',
    image_url: exercise?.image_url || '',
    image_path: exercise?.image_path || '',
    // Execution guidance (EN + ES — AI auto-fill provides both; ES also
    // auto-translated on-demand if left empty)
    execution_steps: exercise?.execution_steps || [],
    execution_steps_es: exercise?.execution_steps_es || [],
    form_tips: exercise?.form_tips || [],
    form_tips_es: exercise?.form_tips_es || [],
    common_mistakes: exercise?.common_mistakes || [],
    common_mistakes_es: exercise?.common_mistakes_es || [],
    benefits: exercise?.benefits || [],
    benefits_es: exercise?.benefits_es || [],
    // Anatomical muscle data (Level 4)
    target_muscles: exercise?.target_muscles || [],
    target_muscles_es: exercise?.target_muscles_es || [],
    synergist_muscles: exercise?.synergist_muscles || [],
    synergist_muscles_es: exercise?.synergist_muscles_es || [],
    stabilizer_muscles: exercise?.stabilizer_muscles || [],
    stabilizer_muscles_es: exercise?.stabilizer_muscles_es || [],
  });

  // AI auto-fill — completes ONLY empty fields (never overwrites trainer input)
  const handleAiAutofill = async () => {
    if (!formData.name.trim()) {
      setAiError('Enter the exercise name first');
      return;
    }
    const isEmpty = (v) => v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === '');
    // Ask the AI only for the fields the trainer left empty — a full-schema
    // generation took ~35s and could outlive the gateway timeout.
    const emptyFields = AI_FILLABLE_FIELDS.filter(f => isEmpty(formData[f]));
    if (emptyFields.length === 0) {
      setAiFilled(false);
      setAiError('Nothing to complete — all AI fields are already filled');
      return;
    }
    setAiLoading(true);
    setAiError(null);
    setAiFilled(false);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/exercises/ai-generate`,
        {
          name: formData.name,
          category: formData.category,
          fields: emptyFields,
          equipment: (formData.equipment_ids || []).map(id => {
            const eq = equipmentList.find(e => e.id === id);
            return eq ? eq.name : id;
          }),
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      const c = res.data.content || {};
      setFormData(prev => {
        const merged = { ...prev };
        Object.keys(c).forEach(key => {
          if (key in merged && isEmpty(merged[key]) && !isEmpty(c[key])) {
            // Respect the max-2 rule for primary muscle groups
            merged[key] = key === 'primary_muscle_groups' ? c[key].slice(0, 2) : c[key];
          }
        });
        return merged;
      });
      setAiFilled(true);
    } catch (err) {
      console.error('AI auto-fill error:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setAiError('AI took too long. Try again or fill fewer fields at once.');
      } else {
        setAiError(err.response?.data?.detail || 'AI auto-fill failed. Try again.');
      }
    } finally {
      setAiLoading(false);
    }
  };

  // Load equipment reference data
  useEffect(() => {
    const loadEquipment = async () => {
      try {
        const response = await axios.get(`${API}/reference/equipment`);
        setEquipmentList(response.data.equipment || []);
      } catch (error) {
        console.error('Error loading equipment:', error);
      }
    };
    loadEquipment();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingImg(true);
    setImgError(null);
    try {
      const data = await uploadExerciseImage(file);
      setFormData(prev => ({ ...prev, image_url: data.image_url, image_path: data.image_path }));
    } catch (err) {
      setImgError(err.response?.data?.detail || t('image_upload_failed'));
    } finally {
      setUploadingImg(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate max 2 primary muscles
    if (formData.primary_muscle_groups.length > 2) {
      alert('Maximum 2 primary muscle groups allowed');
      return;
    }
    
    // Convert empty exercise_family string to null so the DB doesn't
    // store empty strings (which would mess with "no family" filters).
    const rawSecs = String(formData.avg_set_seconds).trim();
    const payload = {
      ...formData,
      exercise_family: formData.exercise_family ? formData.exercise_family : null,
      // Manual duration override: empty → null (revert to learned/default).
      avg_set_seconds: rawSecs === '' ? null : Number(rawSecs),
      image_path: formData.image_path || null,
    };
    
    await onSave(payload);
  };

  const togglePrimaryMuscle = (muscle) => {
    const current = formData.primary_muscle_groups || [];
    if (current.includes(muscle)) {
      setFormData({...formData, primary_muscle_groups: current.filter(m => m !== muscle)});
    } else if (current.length < 2) {
      setFormData({...formData, primary_muscle_groups: [...current, muscle]});
    } else {
      alert('Maximum 2 primary muscle groups allowed');
    }
  };

  const toggleEquipment = (equipmentId) => {
    const current = formData.equipment_ids || [];
    if (current.includes(equipmentId)) {
      setFormData({...formData, equipment_ids: current.filter(e => e !== equipmentId)});
    } else {
      setFormData({...formData, equipment_ids: [...current, equipmentId]});
    }
  };

  // Helper to handle array fields (execution_steps, form_tips, etc.)
  const handleArrayField = (field, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setFormData({...formData, [field]: items});
  };

  const getArrayFieldValue = (field) => {
    return (formData[field] || []).join('\n');
  };

  const tabs = [
    { id: 'basic', label: '📝 Basic Info', icon: '📝' },
    { id: 'execution', label: '🎯 Execution', icon: '🎯' },
    { id: 'muscles', label: '💪 Muscles', icon: '💪' },
    { id: 'advanced', label: '⚙️ Advanced', icon: '⚙️' },
  ];

  return (
    <Modal onClose={onClose} maxWidth="max-w-5xl">
      <Modal.Header onClose={onClose}>
        <div className="flex items-center justify-between w-full gap-4">
          <h2 className="text-xl font-bold text-white" data-testid="exercise-modal-title">
            {isCreate ? (t('add_exercise') || 'Add Exercise') : `${t('edit_exercise')}: ${exercise?.name}`}
          </h2>
          <div className="flex items-center gap-2">
            {aiFilled && !aiLoading && (
              <span className="text-xs text-green-300" data-testid="ai-filled-badge">✓ AI filled empty fields</span>
            )}
            {aiError && (
              <span className="text-xs text-red-300" data-testid="ai-error-msg">{aiError}</span>
            )}
            <button
              type="button"
              onClick={handleAiAutofill}
              disabled={aiLoading}
              className="px-3 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-500 hover:to-indigo-500 text-white transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
              data-testid="ai-autofill-btn"
              title="Complete empty fields with AI"
            >
              {aiLoading ? (
                <><span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span> Generating…</>
              ) : (
                <>✨ Auto-fill with AI</>
              )}
            </button>
          </div>
        </div>
      </Modal.Header>
        
      <Modal.Body className="p-0">
        {/* Tabs */}
        <div className="border-b border-blue-400/30 px-4 pt-4">
          <div className="flex space-x-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-t-lg transition text-sm ${
                  activeTab === tab.id 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-900/30 text-blue-300 hover:bg-blue-800/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form id="exercise-edit-form" onSubmit={handleSubmit} className="p-4 overflow-y-auto max-h-[60vh] space-y-6">
            
            {/* BASIC INFO TAB */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                {/* Name and Spanish Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">{t('exercise_name')} (EN)</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">{t('exercise_name')} (ES)</label>
                    <input
                      type="text"
                      value={formData.name_es}
                      onChange={(e) => setFormData({...formData, name_es: e.target.value})}
                      className="input-field"
                      placeholder="Nombre en español..."
                    />
                  </div>
                </div>

                {/* Classification Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">📂 Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="input-field"
                      required
                    >
                      <option value="">Select Category</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{t(cat.id) || cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">⭐ Difficulty</label>
                    <select
                      value={formData.difficulty}
                      onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                      className="input-field"
                      required
                    >
                      <option value="">Select Difficulty</option>
                      <option value="beginner">{t('beginner')}</option>
                      <option value="intermediate">{t('intermediate')}</option>
                      <option value="advanced">{t('advanced')}</option>
                    </select>
                  </div>
                </div>

                {/* Movement Pattern & Exercise Type (v3) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">Movement Pattern</label>
                    <select
                      value={formData.movement_pattern}
                      onChange={(e) => setFormData({...formData, movement_pattern: e.target.value})}
                      className="input-field"
                      data-testid="edit-movement-pattern"
                    >
                      <option value="">Select Pattern</option>
                      {MOVEMENT_PATTERNS.map(mp => (
                        <option key={mp.id} value={mp.id}>{mp.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">Exercise Type</label>
                    <select
                      value={formData.exercise_type}
                      onChange={(e) => setFormData({...formData, exercise_type: e.target.value})}
                      className="input-field"
                      data-testid="edit-exercise-type"
                    >
                      <option value="">Select Type</option>
                      {EXERCISE_TYPES.map(et => (
                        <option key={et.id} value={et.id}>{et.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Exercise Family (Phase 1A) — used for swap suggestions */}
                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">
                    🧬 Exercise Family <span className="text-blue-400/60 text-xs font-normal">(used for swap suggestions)</span>
                  </label>
                  <select
                    value={formData.exercise_family}
                    onChange={(e) => setFormData({...formData, exercise_family: e.target.value})}
                    className="input-field"
                    data-testid="edit-exercise-family"
                  >
                    <option value="">— No family —</option>
                    {EXERCISE_FAMILIES.map(fam => (
                      <option key={fam.id} value={fam.id}>{fam.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-blue-300/60 mt-1">
                    Group exercises that a trainer would realistically swap for each other (e.g., all bench press variations).
                  </p>
                </div>

                {/* Image URL */}
                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">🖼️ {t('image_url')}</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value, image_path: ''})}
                    className="input-field"
                    placeholder="https://example.com/exercise.gif"
                    data-testid="exercise-image-url-input"
                  />
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-blue-300 text-xs">{t('or_label')}</span>
                    <label
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition ${uploadingImg ? 'bg-slate-600 text-slate-300 cursor-wait' : 'bg-cyan-500/90 hover:bg-cyan-400 text-white'}`}
                      data-testid="exercise-image-upload-label"
                    >
                      <span>📁</span>
                      {uploadingImg ? t('uploading_image') : t('upload_image')}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingImg}
                        onChange={handleImageUpload}
                        data-testid="exercise-image-upload-input"
                      />
                    </label>
                  </div>
                  {imgError && (
                    <p className="text-red-400 text-xs mt-1" data-testid="exercise-image-upload-error">{imgError}</p>
                  )}
                  {formData.image_url && (
                    <div className="mt-2">
                      <img
                        src={formData.image_url}
                        alt="Exercise preview"
                        className="max-w-xs h-32 object-contain rounded-lg bg-black/20"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        data-testid="exercise-image-preview"
                      />
                    </div>
                  )}
                </div>

                {/* Equipment */}
                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">🔧 Equipment</label>
                  <div className="flex flex-wrap gap-2">
                    {equipmentList.map(eq => (
                      <button
                        key={eq.id}
                        type="button"
                        onClick={() => toggleEquipment(eq.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                          formData.equipment_ids?.includes(eq.id)
                            ? 'bg-cyan-500 text-white'
                            : 'bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/40'
                        }`}
                      >
                        {language === 'es' ? eq.name_es : eq.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Primary Muscle Groups */}
                <div>
                  <label className="block text-blue-200 text-sm font-medium mb-2">
                    💪 Primary Muscle Groups <span className="text-purple-300">(max 2)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PRIMARY_MUSCLE_GROUPS.map(muscle => (
                      <button
                        key={muscle}
                        type="button"
                        onClick={() => togglePrimaryMuscle(muscle)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                          formData.primary_muscle_groups?.includes(muscle)
                            ? 'bg-purple-500 text-white'
                            : 'bg-purple-500/20 text-purple-200 hover:bg-purple-500/40'
                        }`}
                      >
                        {muscle}
                      </button>
                    ))}
                  </div>
                  {formData.primary_muscle_groups?.length > 0 && (
                    <div className="mt-2 text-sm text-purple-300">
                      Selected: {formData.primary_muscle_groups.join(', ')} ({formData.primary_muscle_groups.length}/2)
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* EXECUTION TAB */}
            {activeTab === 'execution' && (
              <div className="space-y-6">
                {/* How to Perform (Execution Steps) */}
                <div className="bg-green-900/20 rounded-lg p-4 border border-green-400/30">
                  <h3 className="text-lg font-semibold text-green-200 mb-2">🎯 How to Perform (Execution Steps)</h3>
                  <p className="text-green-300/70 text-sm mb-4">
                    Single source of truth for exercise execution. One step per line. AI translates automatically.
                  </p>
                  <textarea
                    value={getArrayFieldValue('execution_steps')}
                    onChange={(e) => handleArrayField('execution_steps', e.target.value)}
                    className="input-field border-green-500/30"
                    rows="8"
                    placeholder="Position yourself on the bench&#10;Grip the bar slightly wider than shoulder-width&#10;Lower the bar to mid-chest&#10;Press the bar back up explosively&#10;Lock out at the top..."
                  />
                  <p className="text-xs text-green-400/60 mt-1">
                    {formData.execution_steps?.length || 0} steps defined
                  </p>
                </div>

                {/* Common Mistakes */}
                <div className="bg-red-900/20 rounded-lg p-4 border border-red-400/30">
                  <h3 className="text-lg font-semibold text-red-200 mb-2">⚠️ Common Mistakes</h3>
                  <p className="text-red-300/70 text-sm mb-4">
                    What to avoid when performing this exercise. One mistake per line.
                  </p>
                  <textarea
                    value={getArrayFieldValue('common_mistakes')}
                    onChange={(e) => handleArrayField('common_mistakes', e.target.value)}
                    className="input-field border-red-500/30"
                    rows="5"
                    placeholder="Arching the back too much&#10;Using momentum instead of muscle&#10;Not going through full range of motion..."
                  />
                  <p className="text-xs text-red-400/60 mt-1">
                    {formData.common_mistakes?.length || 0} mistakes listed
                  </p>
                </div>

                {/* Benefits */}
                <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-400/30">
                  <h3 className="text-lg font-semibold text-blue-200 mb-2">💎 Benefits</h3>
                  <p className="text-blue-300/70 text-sm mb-4">
                    Why clients should do this exercise. One benefit per line.
                  </p>
                  <textarea
                    value={getArrayFieldValue('benefits')}
                    onChange={(e) => handleArrayField('benefits', e.target.value)}
                    className="input-field border-blue-500/30"
                    rows="5"
                    placeholder="Builds chest strength and size&#10;Improves pressing power&#10;Engages stabilizer muscles..."
                  />
                  <p className="text-xs text-blue-400/60 mt-1">
                    {formData.benefits?.length || 0} benefits listed
                  </p>
                </div>

                {/* Form Tips */}
                <div className="bg-yellow-900/20 rounded-lg p-4 border border-yellow-400/30">
                  <h3 className="text-lg font-semibold text-yellow-200 mb-2">✨ Form Tips</h3>
                  <p className="text-yellow-300/70 text-sm mb-4">
                    Key technique points for proper form. One tip per line.
                  </p>
                  <textarea
                    value={getArrayFieldValue('form_tips')}
                    onChange={(e) => handleArrayField('form_tips', e.target.value)}
                    className="input-field border-yellow-500/30"
                    rows="4"
                    placeholder="Keep your core engaged throughout&#10;Maintain a neutral spine&#10;Control the movement on the way down..."
                  />
                  <p className="text-xs text-yellow-400/60 mt-1">
                    {formData.form_tips?.length || 0} tips defined
                  </p>
                </div>
              </div>
            )}

            {/* MUSCLES TAB (Anatomical) */}
            {activeTab === 'muscles' && (
              <div className="space-y-6">
                <div className="bg-orange-900/20 rounded-lg p-4 border border-orange-400/30">
                  <p className="text-orange-200 text-sm">
                    ℹ️ <strong>Anatomical Muscle Data</strong> is for educational and analytical purposes. 
                    It is shown in detailed exercise views and used for AI recommendations, but NOT for main filtering.
                  </p>
                </div>

                {/* Target Muscles */}
                <div>
                  <label className="block text-orange-200 text-sm font-medium mb-2">
                    🎯 Target Muscles <span className="text-orange-400/70">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={(formData.target_muscles || []).join(', ')}
                    onChange={(e) => setFormData({...formData, target_muscles: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                    className="input-field"
                    placeholder="e.g., Pectoralis Major (Sternal), Anterior Deltoid"
                  />
                  <p className="text-xs text-orange-400/60 mt-1">
                    Primary muscles being worked. Use anatomical names.
                  </p>
                </div>

                {/* Synergist Muscles */}
                <div>
                  <label className="block text-orange-200 text-sm font-medium mb-2">
                    🤝 Synergist Muscles <span className="text-orange-400/70">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={(formData.synergist_muscles || []).join(', ')}
                    onChange={(e) => setFormData({...formData, synergist_muscles: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                    className="input-field"
                    placeholder="e.g., Triceps Brachii, Serratus Anterior"
                  />
                  <p className="text-xs text-orange-400/60 mt-1">
                    Secondary muscles that assist the movement.
                  </p>
                </div>

                {/* Stabilizer Muscles */}
                <div>
                  <label className="block text-orange-200 text-sm font-medium mb-2">
                    ⚖️ Stabilizer Muscles <span className="text-orange-400/70">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={(formData.stabilizer_muscles || []).join(', ')}
                    onChange={(e) => setFormData({...formData, stabilizer_muscles: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                    className="input-field"
                    placeholder="e.g., Rotator Cuff, Core, Biceps Brachii"
                  />
                  <p className="text-xs text-orange-400/60 mt-1">
                    Muscles that stabilize joints during the movement.
                  </p>
                </div>

                {/* Summary */}
                {(formData.target_muscles?.length > 0 || formData.synergist_muscles?.length > 0 || formData.stabilizer_muscles?.length > 0) && (
                  <div className="bg-gray-900/40 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-3">Muscle Summary</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-orange-300">Target:</span>
                        <div className="text-white">{formData.target_muscles?.length || 0} muscles</div>
                      </div>
                      <div>
                        <span className="text-orange-300">Synergist:</span>
                        <div className="text-white">{formData.synergist_muscles?.length || 0} muscles</div>
                      </div>
                      <div>
                        <span className="text-orange-300">Stabilizer:</span>
                        <div className="text-white">{formData.stabilizer_muscles?.length || 0} muscles</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ADVANCED TAB */}
            {activeTab === 'advanced' && (
              <div className="space-y-6">
                {/* Duration estimate (HYBRID: manual override + auto-learned) */}
                {(() => {
                  const learned = exercise?.learned_avg_set_seconds;
                  const eff = effectiveSetSeconds({
                    avg_set_seconds: String(formData.avg_set_seconds).trim() === '' ? null : Number(formData.avg_set_seconds),
                    learned_avg_set_seconds: learned,
                    category: formData.category,
                    exercise_type: formData.exercise_type,
                  });
                  const sourceLabel = { manual: 'Manual override', learned: 'Auto-learned', default: 'Category default' }[eff.source];
                  return (
                    <div className="bg-indigo-900/20 rounded-lg p-4 border border-indigo-400/30" data-testid="duration-estimate-section">
                      <h4 className="text-white font-medium mb-1">⏱️ Duration estimate</h4>
                      <p className="text-indigo-300/70 text-sm mb-4">
                        Work-seconds per set used by the planning estimate & the in-workout ETA.
                        Priority: <strong>manual → auto-learned → category default</strong>. Leave blank to auto-learn.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                        <div>
                          <label className="block text-indigo-200 text-sm font-medium mb-2">Manual override (sec/set)</label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={formData.avg_set_seconds}
                            onChange={(e) => setFormData({ ...formData, avg_set_seconds: e.target.value })}
                            className="input-field"
                            placeholder="e.g. 45"
                            data-testid="edit-avg-set-seconds"
                          />
                          {String(formData.avg_set_seconds).trim() !== '' && (
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, avg_set_seconds: '' })}
                              className="text-xs text-indigo-300/70 hover:text-indigo-200 mt-1"
                              data-testid="clear-avg-set-seconds"
                            >
                              ✕ Clear (use auto-learned)
                            </button>
                          )}
                        </div>
                        <div>
                          <span className="block text-indigo-200 text-sm font-medium mb-2">Auto-learned</span>
                          <div className="text-white text-lg font-semibold" data-testid="learned-avg-set-seconds">
                            {learned != null ? `${Math.round(learned)}s` : '—'}
                          </div>
                          <p className="text-xs text-indigo-300/50 mt-1">
                            {learned != null ? 'From completed sessions' : 'Not enough sessions yet'}
                          </p>
                        </div>
                        <div>
                          <span className="block text-indigo-200 text-sm font-medium mb-2">Effective (used)</span>
                          <div className="text-emerald-300 text-lg font-semibold" data-testid="effective-avg-set-seconds">
                            {Math.round(eff.seconds)}s
                          </div>
                          <p className="text-xs text-indigo-300/50 mt-1">{sourceLabel}</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="bg-gray-900/40 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Exercise Metadata</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-blue-300">ID:</span>
                      <div className="text-white font-mono text-xs">{exercise?.id?.slice(0, 8)}...</div>
                    </div>
                    <div>
                      <span className="text-blue-300">AI Enhanced:</span>
                      <div className="text-white">{exercise?.ai_enhanced ? '✅ Yes' : '❌ No'}</div>
                    </div>
                    <div>
                      <span className="text-blue-300">Schema Version:</span>
                      <div className="text-white">{exercise?.schema_version || 1}</div>
                    </div>
                    <div>
                      <span className="text-blue-300">Source:</span>
                      <div className="text-white">{exercise?.source || 'Manual'}</div>
                    </div>
                  </div>
                </div>

                {/* Data Completeness */}
                <div className="bg-gray-900/40 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Data Completeness</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-300">Basic Info</span>
                      <span className={formData.name && formData.category ? 'text-green-400' : 'text-red-400'}>
                        {formData.name && formData.category ? '✅ Complete' : '❌ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-300">Execution Steps</span>
                      <span className={formData.execution_steps?.length > 0 ? 'text-green-400' : 'text-yellow-400'}>
                        {formData.execution_steps?.length > 0 ? `✅ ${formData.execution_steps.length} steps` : '⚠️ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-300">Common Mistakes</span>
                      <span className={formData.common_mistakes?.length > 0 ? 'text-green-400' : 'text-yellow-400'}>
                        {formData.common_mistakes?.length > 0 ? `✅ ${formData.common_mistakes.length} items` : '⚠️ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-300">Benefits</span>
                      <span className={formData.benefits?.length > 0 ? 'text-green-400' : 'text-yellow-400'}>
                        {formData.benefits?.length > 0 ? `✅ ${formData.benefits.length} items` : '⚠️ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-300">Form Tips</span>
                      <span className={formData.form_tips?.length > 0 ? 'text-green-400' : 'text-yellow-400'}>
                        {formData.form_tips?.length > 0 ? `✅ ${formData.form_tips.length} tips` : '⚠️ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-300">Image/GIF</span>
                      <span className={formData.image_url ? 'text-green-400' : 'text-yellow-400'}>
                        {formData.image_url ? '✅ Present' : '⚠️ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-300">Anatomical Muscles</span>
                      <span className={formData.target_muscles?.length > 0 ? 'text-green-400' : 'text-yellow-400'}>
                        {formData.target_muscles?.length > 0 ? '✅ Present' : '⚠️ Missing'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Buttons - Always visible */}
          </form>
      </Modal.Body>
      
      <Modal.Footer className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition"
        >
          {t('cancel')}
        </button>
        <button
          type="submit"
          form="exercise-edit-form"
          className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition font-medium"
          data-testid="exercise-modal-save-btn"
        >
          {isCreate ? (t('add_exercise') || 'Add Exercise') : t('save_changes')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExerciseEditModal;
